import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { fmt } from '../utils/format';
import { Chart } from 'chart.js/auto';

export default function Dashboard() {
  const { state, cl } = useApp();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('theme-change', handleThemeChange);
    return () => window.removeEventListener('theme-change', handleThemeChange);
  }, []);

  const pInv = state.inward.filter(x => x.status === 'pending').length;
  const pWC  = state.workcards.filter(x => x.status === 'pending').length;
  const clientCount = state.clients.length;

  const stockSheetsSum = useMemo(() => {
    return state.stockBalance.reduce((sum, b) => sum + (b.balance_sheets || 0), 0);
  }, [state.stockBalance]);

  const outstandingAmount = useMemo(() => {
    // Include invoices that are not marked as paid
    return state.invoices
      .filter(x => x.status !== 'paid')
      .reduce((sum, inv) => sum + (inv.balance_due || 0), 0);
  }, [state.invoices]);

  // Combine and sort live bot uploads
  const uploads = useMemo(() => {
    const list = [];
    
    state.inward.forEach(item => {
      list.push({
        type: 'invoice',
        title: 'Supplier Invoice Uploaded',
        subtitle: `${item.supplier} - #${item.inv_no}`,
        time: item.recv_at ? new Date(item.recv_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
        rawTime: item.recv_at || '',
        status: item.status,
        thumb: 'https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?auto=format&fit=crop&q=80&w=100'
      });
    });

    state.workcards.forEach(item => {
      const client = cl(item.client_id);
      const clientLabel = client ? client.name : 'Unlinked Customer';
      list.push({
        type: 'ticket',
        title: 'Work Ticket Received',
        subtitle: `${clientLabel} - Qty: ${item.qty?.toLocaleString()} (${item.gsm} GSM)`,
        time: item.recv_at ? new Date(item.recv_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
        rawTime: item.recv_at || '',
        status: item.status,
        thumb: 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?auto=format&fit=crop&q=80&w=100'
      });
    });

    return list.sort((a, b) => b.rawTime.localeCompare(a.rawTime)).slice(0, 4);
  }, [state.inward, state.workcards, cl]);

  // Build chart data from real ledger movements (last 6 months)
  const chartData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        label: d.toLocaleString('default', { month: 'short' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        inward: 0,
        consumed: 0,
      });
    }
    state.ledger.forEach(entry => {
      const date = entry.date ? new Date(entry.date) : null;
      if (!date) return;
      const m = months.find(mo => mo.year === date.getFullYear() && mo.month === date.getMonth());
      if (!m) return;
      if (entry.type === 'inward')   m.inward   += (entry.qty || 0);
      if (entry.type === 'consumed') m.consumed += (entry.qty || 0);
    });
    return {
      labels:   months.map(m => m.label),
      inward:   months.map(m => m.inward),
      consumed: months.map(m => m.consumed),
    };
  }, [state.ledger]);

  // Render chart
  useEffect(() => {
    const ctx = document.getElementById('stockMovementChart')?.getContext('2d');
    if (!ctx) return;

    const isLight = theme === 'light';

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.labels,
        datasets: [
          {
            label: 'Inward Stock (Sheets)',
            data: chartData.inward,
            borderColor: '#00f2fe',
            backgroundColor: 'rgba(0, 242, 254, 0.05)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
          },
          {
            label: 'Consumed Stock (Sheets)',
            data: chartData.consumed,
            borderColor: '#7f00ff',
            backgroundColor: 'rgba(127, 0, 255, 0.05)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: { color: isLight ? '#475569' : '#94a3b8', font: { family: 'Outfit' } },
          },
          tooltip: {
            callbacks: {
              label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} sheets`,
            },
          },
        },
        scales: {
          x: {
            grid: { color: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.02)' },
            ticks: { color: isLight ? '#475569' : '#64748b' },
          },
          y: {
            beginAtZero: true,
            grid: { color: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.02)' },
            ticks: { color: isLight ? '#475569' : '#64748b' },
          },
        },
      },
    });

    return () => {
      chart.destroy();
    };
  }, [theme, chartData]);


  return (
    <Layout>
      <div className="dashboard-scroll-wrapper">
      {/* Quick Stats Cards Grid */}
      <div className="stats-grid">
        <div className="stat-card card-glow-purple">
          <div className="stat-icon"><i className="fa-solid fa-file-invoice-dollar"></i></div>
          <div className="stat-details">
            <h3>Pending Inward</h3>
            <div className="value">{pInv}</div>
            <p className="trend negative"><i className="fa-solid fa-exclamation-triangle"></i> Needs review</p>
          </div>
        </div>
        <div className="stat-card card-glow-cyan">
          <div className="stat-icon"><i className="fa-solid fa-ticket"></i></div>
          <div className="stat-details">
            <h3>Pending Tickets</h3>
            <div className="value">{pWC}</div>
            <p className="trend warning"><i className="fa-solid fa-clock"></i> Awaiting approval</p>
          </div>
        </div>
        <div className="stat-card card-glow-green">
          <div className="stat-icon"><i className="fa-solid fa-user-check"></i></div>
          <div className="stat-details">
            <h3>Active Clients</h3>
            <div className="value">{clientCount}</div>
            <p className="trend positive"><i className="fa-solid fa-arrow-trend-up"></i> +2 this month</p>
          </div>
        </div>
        <div className="stat-card card-glow-blue">
          <div className="stat-icon"><i className="fa-solid fa-layer-group"></i></div>
          <div className="stat-details">
            <h3>Stock Sheets</h3>
            <div className="value">{stockSheetsSum.toLocaleString()}</div>
            <p className="trend positive"><i className="fa-solid fa-check-circle"></i> Balanced</p>
          </div>
        </div>
        <div className="stat-card card-glow-orange">
          <div className="stat-icon"><i className="fa-solid fa-indian-rupee-sign"></i></div>
          <div className="stat-details">
            <h3>Outstanding Amount</h3>
            <div className="value" style={{ fontSize: outstandingAmount > 99999 ? 20 : 24 }}>{fmt(outstandingAmount)}</div>
            <p className="trend warning"><i className="fa-solid fa-clock"></i> Unpaid invoices</p>
          </div>
        </div>
      </div>

      <div className="dashboard-row">
        {/* Monthly Stock Movement Graph */}
        <div className="chart-card glass-card">
          <div className="card-header">
            <h3>Monthly Stock Movement</h3>
            <span className="header-meta">Sheets (Inward vs. Consumed)</span>
          </div>
          <div className="chart-container">
            <canvas id="stockMovementChart"></canvas>
          </div>
        </div>

        {/* Recent Telegram Activity */}
        <div className="activity-card glass-card">
          <div className="card-header">
            <h3>Recent Telegram Uploads</h3>
            <span className="badge badge-outline">Live Bot feed</span>
          </div>
          <div className="activity-list">
            {uploads.length === 0 ? (
              <div className="placeholder-text">No bot uploads yet.</div>
            ) : (
              uploads.map((item, idx) => {
                let badgeClass = 'badge-outline';
                if (item.status === 'pending') badgeClass = 'badge-warning';
                if (item.status === 'confirmed' || item.status === 'in_progress') badgeClass = 'badge-info';
                if (item.status === 'completed') badgeClass = 'badge-success';

                return (
                  <div key={idx} className="activity-item">
                    <img src={item.thumb} className="activity-thumb" alt="Upload thumb" />
                    <div className="activity-details">
                      <h4>{item.title}</h4>
                      <p>{item.subtitle}</p>
                    </div>
                    <div className="activity-meta">
                      <span>{item.time}</span>
                      <span className={`badge ${badgeClass}`} style={{ textTransform: 'uppercase', fontSize: 9 }}>{item.status}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
}
